import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import { Table, Spinner, Alert, Card, Button, Nav } from 'react-bootstrap';
import { fetchUserOrders, removeOrderItem, cancelOrder } from '../http/orderAPI';
import { fetchStores } from '../http/productAPI';
import { Context } from '../index';
import './UserOrdersMobile.css'; // для мобильных стилей
import { getImageUrl } from '../utils/imageUtils';

function Lightbox({ src, alt, onClose }) {
    if (!src) return null;
    return (
        <div className="lightbox-backdrop" onClick={onClose}>
            <div className="lightbox-content" onClick={e => e.stopPropagation()}>
                <img src={src} alt={alt} className="lightbox-img" />
                <button className="lightbox-close" onClick={onClose}>✖</button>
            </div>
        </div>
    );
}

const UserOrders = () => {
    const { user } = useContext(Context);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lightboxImg, setLightboxImg] = useState(null);
    const [actionLoading, setActionLoading] = useState(''); // orderId или itemId
    const [stores, setStores] = useState([]);
    const [activeTab, setActiveTab] = useState('active'); // 'active' | 'completed'

    const statusMap = {
        pending: 'В ожидании',
        processing: 'В обработке',
        delivered: 'Доставлен',
        completed: 'Выполнен'
    };

    const loadOrders = useCallback(async () => {
        // Не загружаем заказы, если пользователь не авторизован
        if (!user.isAuth) {
            return;
        }
        
        setLoading(true);
        setError('');
        try {
            const data = await fetchUserOrders();
            setOrders(data);
        } catch (e) {
            setError('Ошибка при загрузке заказов');
        } finally {
            setLoading(false);
        }
    }, [user.isAuth]);

    useEffect(() => {
        if (user.isAuth) {
        loadOrders();
        fetchStores().then(setStores);
        }
    }, [user.isAuth, loadOrders]);

    const handleRemoveItem = async (itemId) => {
        if (!window.confirm('Удалить этот товар из заказа? Товар вернётся на склад.')) return;
        setActionLoading(itemId);
        try {
            await removeOrderItem(itemId);
            await loadOrders();
        } catch (e) {
            alert('Ошибка при удалении товара');
        } finally {
            setActionLoading('');
        }
    };

    const handleCancelOrder = async (orderId) => {
        if (!window.confirm('Отменить весь заказ? Все товары вернутся на склад.')) return;
        setActionLoading(orderId);
        try {
            await cancelOrder(orderId);
            await loadOrders();
        } catch (e) {
            alert('Ошибка при отмене заказа');
        } finally {
            setActionLoading('');
        }
    };

    const getStoreName = (storeId) => {
        const store = stores.find(s => String(s.id) === String(storeId));
        return store ? store.name : 'Магазин';
    };

    const { activeOrders, completedOrders } = useMemo(() => {
        const active = orders.filter(o => o.status !== 'completed');
        const completed = orders.filter(o => o.status === 'completed');
        return {
            activeOrders: active.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
            completedOrders: completed.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        };
    }, [orders]);

    const ordersToShow = activeTab === 'active' ? activeOrders : completedOrders;
    const isCompletedTab = activeTab === 'completed';

    if (!user.isAuth) {
        return (
            <Card className="p-3 mt-3" style={{background: '#f8fafc', border: 'none', boxShadow: '0 2px 12px #0001'}}>
                <h3 style={{fontWeight: 700, marginBottom: 24}}>Мои заказы</h3>
                <div className="text-center text-muted">
                    Для просмотра заказов необходимо войти в систему
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-3 mt-3" style={{background: '#f8fafc', border: 'none', boxShadow: '0 2px 12px #0001'}}>
            <h3 style={{fontWeight: 700, marginBottom: 24}}>Мои заказы</h3>
            <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'active')}>
                <Nav.Item>
                    <Nav.Link eventKey="active">Активные ({activeOrders.length})</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="completed">Завершённые ({completedOrders.length})</Nav.Link>
                </Nav.Item>
            </Nav>
            {loading && <Spinner animation="border" />}
            {error && <Alert variant="danger">{error}</Alert>}
            {orders.length === 0 && !loading && <div>У вас нет заказов.</div>}
            {orders.length > 0 && ordersToShow.length === 0 && !loading && (
                <div className="text-muted">В этой категории заказов нет.</div>
            )}
            {/* Десктопная таблица */}
            <div className="d-none d-md-block">
            {ordersToShow.map(order => {
                const total = order.order_items.reduce((sum, item) => sum + ((item.price_with_discount !== undefined && item.price_with_discount !== null ? Number(item.price_with_discount) : (item.product?.price || 0)) * item.quantity), 0);
                const discountPercent = Number(order.discount_percent) || 0;
                const discountAmount = discountPercent > 0 ? total * discountPercent / 100 : 0;
                const totalWithDiscount = total - discountAmount;
                const isCompleted = order.status === 'completed';
                return (
                    <div key={order.id} className="mb-4 p-3" style={{ border: '1px solid #e3e8ee', borderRadius: 16, background: '#fff', boxShadow: '0 1px 6px #0001' }}>
                        <div className="mb-2 d-flex flex-wrap align-items-center justify-content-between">
                            <span style={{fontSize: 18, fontWeight: 600}}>
                                Заказ №{order.order_short_id || (order.id ? order.id.slice(0, 8) : order.id)}
                            </span>
                            <span style={{fontSize: 15, color: isCompleted ? '#22c55e' : '#f59e42', fontWeight: 500}}>
                                {statusMap[order.status] || order.status}
                            </span>
                            <span style={{fontSize: 14, color: '#64748b'}}>
                                {new Date(order.created_at).toLocaleString()}
                            </span>
                        </div>
                        <div className="order-store-name">{getStoreName(order.store_id)}</div>
                        <Table responsive>
                            <thead>
                                <tr>
                                    <th>Товар</th>
                                    <th>Фото</th>
                                    <th>Размер</th>
                                    <th>Цвет</th>
                                    <th>Кол-во</th>
                                    <th>Цена</th>
                                    <th>Сумма</th>
                                    <th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {order.order_items.map(item => {
                                    const price = item.price_with_discount !== undefined && item.price_with_discount !== null ? 
                                        Number(item.price_with_discount) : (item.product?.price || 0);
                                    return (
                                        <tr key={item.id} style={{background: price < (item.product?.price || 0) ? '#e0f7fa' : undefined}}>
                                            <td>{item.product?.name}</td>
                                            <td>
                                                {item.product?.image_url && (
                                                    <img src={getImageUrl(item.product.image_url)}
                                                        alt={item.product?.name}
                                                        loading="lazy"
                                                        decoding="async"
                                                        width={50}
                                                        height={50}
                                                        style={{ width: 50, height: 50, objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => setLightboxImg(getImageUrl(item.product.image_url))}
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                {item.product?.type === 'toy' && item.variant?.name 
                                                    ? item.variant.name 
                                                    : (item.variant?.size || item.size?.value || item.size || '-')}
                                            </td>
                                            <td>{item.color || item.variant?.color || '-'}</td>
                                            <td>{item.quantity}</td>
                                            <td>
                                                {price} ₽
                                                {price < (item.product?.price || 0) && (
                                                    <div style={{fontSize: 12, color: '#22c55e'}}>
                                                        -{Math.round(((item.product?.price || 0) - price) / (item.product?.price || 1) * 100)}%
                                                    </div>
                                                )}
                                            </td>
                                            <td>{price * item.quantity} ₽</td>
                                            <td>
                                                {!isCompleted && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveItem(item.id)}
                                                        disabled={actionLoading === item.id}
                                                    >
                                                        {actionLoading === item.id ? '...' : 'Удалить'}
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <div>Сумма: {total} ₽</div>
                            {discountPercent > 0 && (
                                    <>
                                        <div style={{color: '#22c55e'}}>Скидка: -{discountAmount} ₽ ({discountPercent}%)</div>
                                        <div style={{fontSize: 18, fontWeight: 700, color: '#0ea5e9'}}>Итого: {totalWithDiscount} ₽</div>
                                    </>
                                )}
                        </div>
                        {!isCompleted && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => handleCancelOrder(order.id)}
                                    disabled={actionLoading === order.id}
                                >
                                    {actionLoading === order.id ? '...' : 'Отменить заказ'}
                                </Button>
                            )}
                            </div>
                    </div>
                );
            })}
            </div>
            {/* Мобильная версия */}
            <div className="user-orders-mobile d-md-none">
            {ordersToShow.map(order => {
                const total = order.order_items.reduce((sum, item) => sum + ((item.price_with_discount !== undefined && item.price_with_discount !== null ? Number(item.price_with_discount) : (item.product?.price || 0)) * item.quantity), 0);
                const discountPercent = Number(order.discount_percent) || 0;
                const discountAmount = discountPercent > 0 ? total * discountPercent / 100 : 0;
                const totalWithDiscount = total - discountAmount;
                const isCompleted = order.status === 'completed';
                return (
                    <div key={order.id} className="order-card-mobile">
                        <div className="order-card-header">
                            <span className="order-card-id">Заказ №{order.order_short_id || (order.id ? order.id.slice(0, 8) : order.id)}</span>
                            <span className={isCompleted ? 'order-status-completed' : 'order-status-pending'}>
                                {statusMap[order.status] || order.status}
                            </span>
                        </div>
                        <div className="order-card-date">{new Date(order.created_at).toLocaleString()}</div>
                        <div className="order-items-container">
                            {order.order_items.map(item => {
                                const price = item.price_with_discount !== undefined && item.price_with_discount !== null ? 
                                    Number(item.price_with_discount) : (item.product?.price || 0);
                                return (
                                    <div className="order-item-card" key={item.id}>
                                        <div className="order-item-img-wrap">
                                            {item.product?.image_url && (
                                                <img src={getImageUrl(item.product.image_url)}
                                                    alt={item.product?.name}
                                                    loading="lazy"
                                                    decoding="async"
                                                    width={64}
                                                    height={64}
                                                    className="order-item-img"
                                                    onClick={() => setLightboxImg(getImageUrl(item.product.image_url))}
                                                />
                                            )}
                                        </div>
                                        <div className="order-item-info">
                                            <div className="order-item-name">{item.product?.name}</div>
                                            <div className="order-item-details">
                                                <span className="order-item-article">Арт: {item.product?.barcode || '-'}</span>
                                                <span className="order-item-size">Размер: {item.variant?.size || item.size?.value || item.size || '-'}</span>
                                                <span className="order-item-color">Цвет: {item.color || item.variant?.color || '-'}</span>
                                                <span className="order-item-qty">Кол-во: {item.quantity}</span>
                                            </div>
                                            <div className="order-item-price-info">
                                                <span className="order-item-price">{price} ₽</span>
                                                {price < (item.product?.price || 0) && (
                                                    <span className="order-item-discount">
                                                        -{Math.round(((item.product?.price || 0) - price) / (item.product?.price || 1) * 100)}%
                                                    </span>
                                                )}
                                            </div>
                                            <div className="order-item-sum">{price * item.quantity} ₽</div>
                                        </div>
                                        {!isCompleted && (
                                            <button 
                                                className="order-item-delete-btn"
                                                onClick={() => handleRemoveItem(item.id)}
                                                disabled={actionLoading === item.id}
                                            >
                                                {actionLoading === item.id ? '...' : 'Удалить'}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="order-card-summary">
                            <div>Сумма: {total} ₽</div>
                            {discountPercent > 0 && (
                                <div className="order-card-discount">
                                    Скидка: -{discountAmount} ₽ ({discountPercent}%)
                                </div>
                            )}
                            <div className="order-card-total">
                                Итого: {totalWithDiscount} ₽
                            </div>
                        </div>
                        {!isCompleted && (
                            <Button
                                variant="outline-secondary"
                                className="order-card-cancel-btn"
                                disabled={actionLoading === order.id}
                                onClick={() => handleCancelOrder(order.id)}
                            >
                                {actionLoading === order.id ? '...' : 'Отменить заказ'}
                            </Button>
                        )}
                        <div className="order-store-name-mobile">{getStoreName(order.store_id)}</div>
                    </div>
                );
            })}
            </div>
            <Lightbox src={lightboxImg} alt="Фото товара" onClose={() => setLightboxImg(null)} />
        </Card>
    );
};

export default UserOrders; 